/**
 * Where things are, and who may see them.
 *
 * One table for the toolbar, one for the workspace tabs, and `paths` for every
 * in-app link. `app.tsx` builds its routes from the same tables, so a
 * destination that is in the toolbar is a destination that is routed and
 * gated by the same permission — they cannot drift.
 *
 * Shared by the shell (rendering), the router (gating) and the pages
 * (deep links, e.g. an attention item → the right tab), which is why it lives
 * in lib and not in any one of them.
 *
 * Toolbar, never a sidebar (brief §1, `docs/design/super-admin-panel.md`).
 * Two levels: the toolbar holds the platform's pages (`DESTINATIONS`); inside
 * a المنشأة a second bar under it holds that المنشأة's pages
 * (`WORKSPACE_STRIP`), with the switcher at its start.
 */

import type { MessageKey } from './i18n';
import type { AttentionKind, PlatformPermission } from './platform';

/* ------------------------------------------------------------------ */
/* Paths                                                                */
/* ------------------------------------------------------------------ */

/** The pages in a المنشأة's context bar, in bar order. */
export const WORKSPACE_TABS = ['overview', 'content', 'members', 'roles', 'mail', 'microsoft', 'models', 'audit'] as const;

/** A page in the context bar. */
export type WorkspacePage = (typeof WORKSPACE_TABS)[number];

/**
 * Former tabs that are now a section of another page (owner, 2026-10-08):
 * «قوالب البريد» is a section of «البريد», «النشاط» two sections of «نظرة عامة».
 *
 * Their paths stay routed — as redirects into the section — so a bookmark, a
 * ticket and a link written before the merge still land. `sections` maps the
 * old page's own `?section=` onto the new page's.
 */
export const MERGED_TABS = {
  'email-templates': { into: 'mail', section: 'templates', sections: {} },
  activity: { into: 'overview', section: 'activity', sections: { totals: 'activity', trends: 'trends' } },
} as const satisfies Record<string, { into: WorkspacePage; section: string; sections: Readonly<Record<string, string>> }>;

export type MergedTab = keyof typeof MERGED_TABS;

export const paths = {
  signIn: '/sign-in',
  dashboard: '/',
  tenants: '/tenants',
  /**
   * The active المنشآت, highest spend this month first — the tenants page's own `?status=` and
   * `?sort=`. The server orders by amount spent, not by share of cap: it has no near-cap filter.
   */
  tenantsBySpend: '/tenants?status=active&sort=spend',
  tenantNew: '/tenants/new',
  /**
   * `section` is the page's `?section=` (`useSectionParam`); omit it for the page's default section.
   * Only a page in the bar: a merged tab's old name is routed (as a redirect) but never linked to.
   */
  tenant: (id: string, tab: WorkspacePage = 'overview', section?: string) =>
    `/tenants/${encodeURIComponent(id)}/${tab}${section === undefined ? '' : `?section=${encodeURIComponent(section)}`}`,
  permissions: '/permissions',
  models: '/models',
  audit: '/audit',
  /** What a المنشأة gets when it set nothing of its own — today, the default email look. */
  defaults: '/defaults',
  gallery: '/__ui',
} as const;

/* ------------------------------------------------------------------ */
/* The toolbar                                                          */
/* ------------------------------------------------------------------ */

export interface Destination {
  key: 'dashboard' | 'tenants' | 'permissions' | 'models' | 'audit';
  to: string;
  label: MessageKey;
  /** Shown, and routed, only when held. Mirrors the endpoint's `@RequirePlatformPermissions`. */
  permission: PlatformPermission;
  /** Active only on an exact match (the dashboard at `/`). */
  end: boolean;
}

export const DESTINATIONS: readonly Destination[] = [
  { key: 'dashboard', to: paths.dashboard, label: 'nav.dashboard', permission: 'PLATFORM_TENANT_READ', end: true },
  { key: 'tenants', to: paths.tenants, label: 'nav.tenants', permission: 'PLATFORM_TENANT_READ', end: false },
  {
    key: 'permissions',
    to: paths.permissions,
    label: 'nav.permissions',
    permission: 'PLATFORM_TENANT_READ',
    end: false,
  },
  { key: 'models', to: paths.models, label: 'nav.models', permission: 'PLATFORM_TENANT_READ', end: false },
  { key: 'audit', to: paths.audit, label: 'nav.audit', permission: 'PLATFORM_AUDIT_READ', end: false },
];

/**
 * Platform-wide settings, reached from the account menu rather than the
 * toolbar: rarely visited, and not a place an operator works from all day.
 * Gated like a destination — shown and routed only when held.
 */
export interface AccountLink {
  key: 'defaults';
  to: string;
  label: MessageKey;
  permission: PlatformPermission;
}

export const ACCOUNT_LINKS: readonly AccountLink[] = [
  // Read by anyone who reads المنشآت (`GET /email-branding`); changing it is SUPERUSER-only, on the page.
  { key: 'defaults', to: paths.defaults, label: 'nav.defaults', permission: 'PLATFORM_TENANT_READ' },
];

/* ------------------------------------------------------------------ */
/* The workspace tabs                                                   */
/* ------------------------------------------------------------------ */

export interface WorkspaceTabDef {
  tab: WorkspacePage;
  label: MessageKey;
  /** The permission the tab's *reads* need. Writes inside it check `PLATFORM_TENANT_MANAGE` themselves. */
  permission: PlatformPermission;
}

/** The context bar, in order. These are routed as pages. */
export const WORKSPACE_STRIP: readonly WorkspaceTabDef[] = [
  { tab: 'overview', label: 'tab.overview', permission: 'PLATFORM_TENANT_READ' },
  /* Counts and sums only (ATA-36) — the same rank that reads the overview reads these. */
  { tab: 'content', label: 'tab.content', permission: 'PLATFORM_TENANT_READ' },
  { tab: 'members', label: 'tab.members', permission: 'PLATFORM_TENANT_USERS_READ' },
  { tab: 'roles', label: 'tab.roles', permission: 'PLATFORM_TENANT_USERS_READ' },
  { tab: 'mail', label: 'tab.mail', permission: 'PLATFORM_TENANT_READ' },
  { tab: 'microsoft', label: 'tab.microsoft', permission: 'PLATFORM_TENANT_READ' },
  { tab: 'models', label: 'tab.models', permission: 'PLATFORM_TENANT_READ' },
  { tab: 'audit', label: 'tab.audit', permission: 'PLATFORM_AUDIT_READ' },
];

/**
 * Where a merged tab's old URL lands: its host page, with `?section=` mapped
 * from the old page's own section (or the merged section), and every other
 * parameter — `?template=`, `?weeks=` — kept.
 */
export function mergedTabPath(tenantId: string, tab: MergedTab, search: string): string {
  const { into, section, sections } = MERGED_TABS[tab];
  const params = new URLSearchParams(search);
  const old = params.get('section');
  const mapped: Readonly<Record<string, string>> = sections;

  params.set('section', old !== null && Object.hasOwn(mapped, old) ? (mapped[old] ?? section) : section);

  return `${paths.tenant(tenantId, into)}?${params.toString()}`;
}

/** `/tenants/<id>/<tab>[/…]` → `<tab>`, or null when the URL names no page in the bar (the 404 child). */
export function currentTab(pathname: string, tenantId: string): WorkspacePage | null {
  const prefix = `/tenants/${encodeURIComponent(tenantId)}/`;

  if (!pathname.startsWith(prefix)) return null;

  const segment = pathname.slice(prefix.length).split('/')[0] ?? '';

  return WORKSPACE_STRIP.find((def) => def.tab === segment)?.tab ?? null;
}

/**
 * Where the switcher takes the operator in another المنشأة: the page they are
 * on, so comparing two firms' mail settings is one click each — unless that
 * page is not theirs to see, or there is no page (a 404 child), in which case
 * the overview.
 */
export function switchPath(
  tenantId: string,
  tab: WorkspacePage | null,
  has: (...permissions: PlatformPermission[]) => boolean,
): string {
  const def = tab === null ? undefined : WORKSPACE_STRIP.find((entry) => entry.tab === tab);

  return paths.tenant(tenantId, def !== undefined && has(def.permission) ? def.tab : 'overview');
}

/**
 * A المنشأة's logo, served publicly (`LogoController`) — 404 when none is set.
 * The fallback when its branding has not named one: an `<img>` sends no
 * bearer, and this route needs none.
 */
export function tenantLogoPath(tenantId: string): string {
  return `/api/assets/tenant-logo/${encodeURIComponent(tenantId)}`;
}

/** The entries a holder of `has` may see, in table order. */
export function visible<T extends { permission: PlatformPermission }>(
  items: readonly T[],
  has: (...permissions: PlatformPermission[]) => boolean,
): T[] {
  return items.filter((item) => has(item.permission));
}

/** A page in the bar and, optionally, one of its `?section=` values. */
export interface TabTarget {
  tab: WorkspacePage;
  section?: string;
}

/**
 * Where an attention item on the dashboard leads (brief §2.2: "deep-links to
 * the right tab"). Failed documents and spend have no list to show — content
 * is not readable from here — so they land on the counts that explain them.
 * Failed documents: the overview's content section (failed and pending
 * beside the totals).
 */
export const ATTENTION_TAB: Readonly<Record<AttentionKind, TabTarget>> = {
  DOCUMENTS_FAILED: { tab: 'overview', section: 'content' },
  SPEND_NEAR_CAP: { tab: 'models' },
  NO_GENERAL_ADMIN: { tab: 'members' },
  JOIN_REQUESTS_WAITING: { tab: 'members' },
};

/* ------------------------------------------------------------------ */
/* Return-to after sign-in                                              */
/* ------------------------------------------------------------------ */

/**
 * `/sign-in?next=…` for the page the operator was on.
 *
 * Not for `/sign-in` itself — a loop that would bring them back to the form
 * they just filled in.
 */
export function signInPath(location: { pathname: string; search?: string; hash?: string }): string {
  const here = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;

  if (location.pathname === paths.signIn || here === '/') return paths.signIn;

  return `${paths.signIn}?next=${encodeURIComponent(here)}`;
}

/**
 * The `next` parameter, made safe to navigate to.
 *
 * Only an in-app path is accepted. `//evil.example`, `/\evil.example`,
 * `https://…` and `javascript:` are all things a crafted sign-in link could
 * carry, and an open redirect on the operator panel's sign-in is a phishing
 * page with this panel's address on it.
 */
export function safeNext(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return paths.dashboard;

  // A control character or a backslash anywhere is not an in-app path, and
  // browsers normalise `\` to `/` — so `/\evil` would become `//evil`.
  if (!raw.startsWith('/') || raw.startsWith('//') || /[\\\u0000-\u001f]/.test(raw)) return paths.dashboard;

  if (raw === paths.signIn || raw.startsWith(`${paths.signIn}?`)) return paths.dashboard;

  return raw;
}

/** A tenant id in a URL is a UUID or it is not a tenant. Saves a 400 round trip on a mistyped link. */
export function isUuid(value: string | undefined): value is string {
  return value !== undefined && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
